import { Divider, Link, Stack, Tooltip, Typography } from "@mui/material";
import { AttributeTypeEnum } from "@sinch/core/entities/serverEnums";
import { useFormat } from "@sinch/core/hooks/format";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { AttributeTypeEnum as TAttributeTypeEnum } from "@sinch/types/sinch.types";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CountriesList } from "../CountrySelect/query.graphql";
import { CountriesListQuery } from "../CountrySelect/query.types";
import { IdNamePair } from "../StaffDetailPanel/types";
import { findNameById, idToName } from "../StaffDetailPanel/utils";
import { match } from "ts-pattern";
import { MdsIcon } from "@sinch/components";
import { MdsCalendarClockXs } from "@sinch/icons";
import { GalleryAttribute } from "@sinch/components/application/Worker/Attributes/GalleryAttribute";

interface LabelValueProps {
  label: string;
  value: any;
  type?: TAttributeTypeEnum | undefined;
  files?: { name: string; url: string }[] | null;
  options?: IdNamePair[] | null;
  expirationDate?: Date | null;
}

const CountryLabelValue: FC<{ value: any }> = ({ value }) => {
  const { data } = useQueryHandler<CountriesListQuery>(CountriesList);
  const countryName = useMemo(
    () => data?.countries.find((c) => c.code === value)?.name ?? value,
    [data?.countries, value]
  );

  return <Typography variant="descriptionSmall">{countryName}</Typography>;
};

export const AttributeValue: FC<LabelValueProps> = ({ label, value, type, files, options, expirationDate }) => {
  const { t } = useTranslation();
  const { date } = useFormat();

  const valueNode = match(type)
    .with(AttributeTypeEnum.Bool, () => (
      <Typography variant="descriptionSmall">{value ? t("yes") : t("no")}</Typography>
    ))
    .with(AttributeTypeEnum.Choice, () => {
      if (!options) {
        return null;
      }
      return <Typography variant="descriptionSmall">{findNameById(value, options)}</Typography>;
    })
    .with(AttributeTypeEnum.Country, () => <CountryLabelValue value={value} />)
    .with(AttributeTypeEnum.Multiplechoice, AttributeTypeEnum.Range, () => {
      if (!options) {
        return null;
      }
      return <Typography variant="descriptionSmall">{idToName(value, options).join(", ")}</Typography>;
    })
    .with(AttributeTypeEnum.Date, () => (
      <Typography variant="descriptionSmall">{date.medium(value as string)}</Typography>
    ))
    .with(AttributeTypeEnum.Text, AttributeTypeEnum.Int, AttributeTypeEnum.Textarea, () => (
      <Typography variant="descriptionSmall">{value}</Typography>
    ))
    .with(AttributeTypeEnum.File, () => (
      <>
        {files?.map((file) => (
          <Stack key={file.url} direction="row" spacing={1}>
            <Link href={file.url} target="_blank">
              {t("Action.view")}
            </Link>
            <Divider flexItem orientation="vertical" />
            <Link download={file.name} href={file.url}>
              {t("Action.download")}
            </Link>
          </Stack>
        ))}
      </>
    ))
    .with(AttributeTypeEnum.Image, () => (
      <>
        {files?.map((file) => (
          <Stack key={file.url} direction="row" spacing={1}>
            <Link href={file.url} target="_blank">
              <img alt={file.name} height={64} src={file.url} width={64} />
            </Link>
          </Stack>
        ))}
      </>
    ))
    .with(AttributeTypeEnum.Gallery, () => <GalleryAttribute files={files} />)
    .otherwise(() => <Typography variant="descriptionSmall">{value}</Typography>);

  // The match returns null for value types that have nothing to render (e.g. a choice
  // attribute with no options); render nothing rather than a label with no value below it.
  if (valueNode === null) {
    return null;
  }

  return (
    <>
      <Typography
        sx={{
          pt: 1,
        }}
        variant="descriptionTinyBold"
      >
        {label}
      </Typography>
      <Stack alignItems="center" direction="row" gap={1}>
        {valueNode}
        {expirationDate && (
          <Tooltip title={`${t("ExpirationInput.expirationDate")}: ${date.medium(expirationDate)}`}>
            <MdsIcon fontSize="xs" icon={MdsCalendarClockXs} />
          </Tooltip>
        )}
      </Stack>
    </>
  );
};
